import React from "react";
import { NextPage } from "next";
import { logError } from "repoV2/utils/common/error/error";
import { redirectOnFailureInGetInitialProps } from "repoV2/features/Common/modules/URLsNRouting/URLsNRouting.utils";
import { getCountryCodeAndPhoneNumber } from "repoV2/features/Common/utils/strings";
import { ITransactionDetails } from "repoV2/features/Payments/Payments.interface";
import { CUSTOM_CODES_ACTION_TYPES } from "repoV2/features/CustomCodes/constants";
import {
    ACTION_TYPES as FORM_DATA_ACTION_TYPES,
    FORM_KEYS,
} from "repoV2/features/Common/modules/FormData/FormData.constants";
import { debugLogs } from "repoV2/features/Common/modules/DebugLogs/utils/Logs.utils";
import { getFormattedIndianStates } from "repoV2/features/Common/modules/Location/utils/getFormattedIndianStates";
import { LISTING_TYPES } from "repoV2/features/Listing/Listing.constants";
import { IMerchandiseDetails } from "repoV2/features/Listing/Listing.interfaces";
import { callGetMerchandiseVariantsApi } from "repoV2/features/Listing/utils/callGetMerchandiseVariantsApi";
import { callGetTransactionDetailsApi } from "repoV2/features/Payments/utils/callGetTransactionDetailsApi";
import { ICheckoutListingPage } from "./ICheckoutListingPage.interfaces";
import { callGetListingDetailsApi } from "../../utils/apis/ListingDetailsApi/ListingDetailsApi";
import { ICheckoutListingDetails } from "../../utils/apis/ListingDetailsApi/IListingDetailsApi.interface";
import { checkout_form } from "../PaymentDetailsSection/modules/CheckoutForm/CheckoutForm.constants";
import { booking_initial_user_id } from "../../constants/CheckoutPages.constants";
import { ICheckoutCreatorDetails } from "../../utils/apis/CreatorDetailsApi/ICreatorDetailsApi.interface";
import { callGetcreatorDetailsApi } from "../../utils/apis/CreatorDetailsApi/CreatorDetailsApi";
import { getRenewSubscriptionAvailable } from "../../utils/getRenewSubscriptionAvailable";
import CheckoutListingPageBase from "../CheckoutListingPageBase/CheckoutListingPageBase";
import CheckoutListingPageWithPreview from "../CheckoutListingPageWithPreview/CheckoutListingPageWithPreview";
import { callSessionDetailsApi } from "../../utils/apis/SessionDetailsApi/SessionDetailsApi";
import { processCallSessionDetailsApiResponse } from "../PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/utils/processCallSessionDetailsApiResponse";
import { CHECKOUT_PAGES_ACTION_TYPES } from "../../constants/CheckoutPagesReducer";
import { getMetchandiseBasicDetails } from "../../utils/getMerchandiseBasicDetails";

const {
    user_details: {
        field_names: { name, email, country_code, phone, billing_state },
    },
} = checkout_form;

const CheckoutListingPage: NextPage<ICheckoutListingPage.IProps> = props => {
    const {
        hostName,
        listingSlug,
        listingDetails,
        creatorDetails,
        merchandiseDetails,
        initBooking,
        thankyouUuid,
        renewSubscription,
        communityUuid,
        dynamicLinkUuid,
        isPreview,
        showExpressInterestFormInitalValue,
    } = props;

    return (
        <>
            {isPreview ? (
                <CheckoutListingPageWithPreview
                    hostName={hostName}
                    listingSlug={listingSlug}
                    listingDetails={listingDetails}
                    creatorDetails={creatorDetails}
                    initBooking={initBooking}
                    thankyouUuid={thankyouUuid}
                    renewSubscription={renewSubscription}
                    communityUuid={communityUuid}
                    dynamicLinkUuid={dynamicLinkUuid}
                    merchandiseDetails={merchandiseDetails}
                    isPreview={isPreview}
                    showExpressInterestFormInitalValue={
                        showExpressInterestFormInitalValue
                    }
                />
            ) : (
                <CheckoutListingPageBase
                    hostName={hostName}
                    listingSlug={listingSlug}
                    listingDetails={listingDetails}
                    creatorDetails={creatorDetails}
                    merchandiseDetails={merchandiseDetails}
                    initBooking={initBooking}
                    thankyouUuid={thankyouUuid}
                    renewSubscription={renewSubscription}
                    communityUuid={communityUuid}
                    dynamicLinkUuid={dynamicLinkUuid}
                    isPreview={isPreview}
                    showExpressInterestFormInitalValue={
                        showExpressInterestFormInitalValue
                    }
                />
            )}
        </>
    );
};

CheckoutListingPage.getInitialProps = async (
    ctx: ICheckoutListingPage.IGetInitialPropsArgs
): Promise<ICheckoutListingPage.IProps> => {
    const { hostName } = ctx.params;
    const {
        listingSlug,
        init_booking: _initBooking,
        renew_subscription: _renewSubscription,
        thankyou_page_id: thankyouUuid = "",
        transaction: transactionId,
        community: communityUuid = "",
        dynamic_link: dynamicLinkUuid = "",
        is_preview: isPreview = "",
        name: customerName = "",
        email: customerEmail = "",
        phone: customerPhone = "",
    } = ctx.query;
    const initBooking = !!_initBooking;
    const isRenewSubscriptionQueryParam = !!_renewSubscription;

    let listingDetails: ICheckoutListingDetails = {};
    let creatorDetails: ICheckoutCreatorDetails = {};
    let merchandiseDetails: IMerchandiseDetails = {};

    let isRenewSubscription = false;

    let showExpressInterestFormInitalValue: boolean | undefined = false;

    // const transactionDetailsPromiseApi = new Promise();

    const checkoutListingPageApis: Promise<any>[] = [
        callGetListingDetailsApi({
            hostName,
            listingSlug: `${listingSlug}`,
            dynamicLinkUuid: dynamicLinkUuid ? `${dynamicLinkUuid}` : "",
        }),
        callGetcreatorDetailsApi({
            hostName,
            listingSlug: `${listingSlug}`,
            dynamicLinkUuid: dynamicLinkUuid ? `${dynamicLinkUuid}` : "",
        }),
        callSessionDetailsApi({
            hostName,
            listingSlug: `${listingSlug}`,
        }),
    ];

    // incase we have transaction id and thankyoupage uuid, then the
    // user is coming from thankyou page as an upsell, so we need to
    // show the booking foem fields as pre-filled to the user
    if (thankyouUuid && transactionId) {
        checkoutListingPageApis.push(
            callGetTransactionDetailsApi({
                transactionId: `${transactionId}`,
            })
                // We go to catch block here, as this api is being called on server side for checkout page,
                // now even when this api fails, we have to continue rendering checkout page
                .catch(err => {
                    logError({
                        error: err,
                        occuredAt:
                            "repoV2/features/CheckoutPages/modules/CheckoutListingPage/CheckoutListingPage.tsx",
                        when: "calling callGetTransactionDetailsApi",
                    });
                    return { order: {} };
                })
        );
    }

    // Note: when we pass multiple modules (or sections) backend is fetching data for
    // these modules serially. This actually can be done in parallel to improve response time
    try {
        const [
            defaultSectionDetailsResponse,
            creatorDetailsResponse,
            sessionDetailsResponse,
            transactionResponse,
        ] = await Promise.all(checkoutListingPageApis);
        listingDetails = defaultSectionDetailsResponse;
        creatorDetails = creatorDetailsResponse;

        const {
            showExpressInterestForm: showExpressInterestFormNewValue,
            sessionDetails,
        } = processCallSessionDetailsApiResponse({
            data: sessionDetailsResponse,
            listingType: listingDetails.type,
            showExpressInterest: listingDetails.is_express_interest,
        });

        showExpressInterestFormInitalValue = showExpressInterestFormNewValue;

        merchandiseDetails = getMetchandiseBasicDetails(sessionDetailsResponse);

        ctx.store.dispatch({
            type: CHECKOUT_PAGES_ACTION_TYPES.SET_SESSIONS_DATA,
            payload: {
                data: sessionDetails,
                isFetched: true,
                listingUuid: listingDetails.uuid,
            },
        });

        if (isRenewSubscriptionQueryParam && listingDetails.type) {
            isRenewSubscription = getRenewSubscriptionAvailable({
                isRenewSubscription: isRenewSubscriptionQueryParam,
                listingType: listingDetails.type,
                subscriptionDays: listingDetails?.metadata?.subscription_days,
            });
        }

        // Todo: Setup checkout2 route with merchandise, and take page speed with moving this api
        // call with the other checkout server api calls in the Promise.all
        // Use the more optimized one here
        if (listingDetails.type === LISTING_TYPES.MERCHANDISE) {
            // variants api of the merchandise checkout, here we send isHidePurchaseDetails as true,
            // reason, to not fetch price related things on server side, as they may vary on client
            // because of change in timezone
            const merchandiseVariantsResponse =
                await callGetMerchandiseVariantsApi({
                    listingUuid: listingDetails.uuid || "",
                    isHidePurchaseDetails: true,
                });
            const variantsData = merchandiseVariantsResponse?.data?.data || {};

            if (Object.keys(variantsData).length) {
                ctx.store.dispatch({
                    type: CHECKOUT_PAGES_ACTION_TYPES.SET_MERCHANDISE_VARIANTS_DATA,
                    payload: {
                        isFetched: true,
                        listingUuid: listingDetails.uuid,
                        data: {
                            order: variantsData?.variant_type_order || [],
                            variants: variantsData?.variants || {},
                            renderData:
                                variantsData?.variants_display_data || {},
                        },
                    },
                });
            }
        }

        const transactionDetails: ITransactionDetails["order"] =
            transactionResponse?.order || {};

        // If we have both thankyou page uuid, and transactionId, then this implies that it
        // is an upsell scenerio. In such case we need to show the details for the form as pre-filled to
        // the cusomer.
        // We also check, transactionDetails?.order_id, as it implies whetcher the api for transaction data is
        // is returning booking details
        if (thankyouUuid && transactionId && transactionDetails?.order_id) {
            const { indian_states_and_uts_dropdown_map } =
                getFormattedIndianStates();

            // Get the details to show as pre-filled calling this api
            // transactionDetails = transactionResponse?.order;
            const {
                customer_name: userName = "",
                customer_email: userEmail = "",
                customer_phone: completePhoneNumber = "",
                billing_state: gstBillingState = "",
            } = transactionDetails;
            const {
                countryCode: userCountryCode,
                phoneNumber: userPhoneNumber,
            } = getCountryCodeAndPhoneNumber(completePhoneNumber);

            const userBillingState =
                indian_states_and_uts_dropdown_map?.[gstBillingState];

            const userDetails = {
                [name]: userName,
                [email]: userEmail,
                [country_code]: userCountryCode,
                [phone]: userPhoneNumber,
                ...(userBillingState?.value && {
                    [billing_state]: userBillingState,
                }),
            };

            // Here, we'll set these details for the first user in the form data redux,
            // This will pass these values to the form, and as we fetch these on server-side,
            // they'll be available at the time the form and the form fields are registered with
            // react-hook-form, and will be set as default values, pre-filling them.
            ctx.store.dispatch({
                type: FORM_DATA_ACTION_TYPES.SET_FORM_DATA,
                payload: {
                    formKey: `${FORM_KEYS.user_details}.${booking_initial_user_id}`,
                    formData: userDetails,
                },
            });
        }

        ctx.store.dispatch({
            type: CUSTOM_CODES_ACTION_TYPES.SET,
            payload: {
                customCodes: listingDetails.custom_codes,
            },
        });

        if (customerName || customerEmail || customerPhone) {
            const userDetails = {
                [name]: customerName,
                [email]: customerEmail,
                [phone]: customerPhone,
            };
            ctx.store.dispatch({
                type: FORM_DATA_ACTION_TYPES.SET_FORM_DATA,
                payload: {
                    formKey: `${FORM_KEYS.user_details}.${booking_initial_user_id}`,
                    formData: userDetails,
                },
            });
        }
    } catch (err: any) {
        debugLogs({
            data: {
                call: "Calling checkout basic details api for section = 1, 2, 4",
                hostName,
                listingSlug,
                query: ctx.query,
                params: ctx.params,
                ctx,
                headers: ctx?.req?.headers,
                userAgent: ctx?.req?.headers["user-agent"],
                communityUuid,
                dynamicLinkUuid,
            },
            metaData: {},
        });
        logError({
            error: err,
            occuredAt: "CheckoutListingPage.getInitialProps",
            when: "callGetCheckoutPageSectionsApi",
        });
        redirectOnFailureInGetInitialProps(ctx);
    }

    const returnVal = {
        hostName,
        listingSlug: `${listingSlug}`,
        listingDetails,
        showExpressInterestFormInitalValue,
        creatorDetails,
        merchandiseDetails,
        initBooking,
        thankyouUuid: thankyouUuid ? `${thankyouUuid}` : "",
        communityUuid: communityUuid ? `${communityUuid}` : "",
        dynamicLinkUuid: dynamicLinkUuid ? `${dynamicLinkUuid}` : "",
        isPreview: Boolean(isPreview),
        renewSubscription: isRenewSubscription,
    };
    return returnVal;
};

export default CheckoutListingPage;
